import type { AlertColor } from "@mui/material";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

type SnackbarState = {
  snackbarOpen: boolean;
  snackbarAutoCloseDuration: number | null;
  alertSeverity: AlertColor;
  alertMessage: string;
  disableClickaway: boolean;
};

type SnackbarMessageOptions = {
  message: string;
  duration?: number | null;
  disableClickaway?: boolean;
};

type SnackbarMessagePayload = string | SnackbarMessageOptions;

const DEFAULT_SNACKBAR_DURATION = 5000;

const initialState: SnackbarState = {
  snackbarOpen: false,
  snackbarAutoCloseDuration: DEFAULT_SNACKBAR_DURATION,
  alertSeverity: "info",
  alertMessage: "Alert Message",
  disableClickaway: false,
};

export const snackbarSlice = createSlice({
  name: "snackbar",
  initialState,
  reducers: {
    showErrorMessage: (
      state,
      action: PayloadAction<SnackbarMessagePayload>
    ) => {
      const payload =
        typeof action.payload === "string"
          ? {
              message: action.payload,
              duration: DEFAULT_SNACKBAR_DURATION,
              disableClickaway: false,
            }
          : action.payload;
      state.alertMessage = payload.message;
      state.snackbarOpen = true;
      state.alertSeverity = "error";
      state.snackbarAutoCloseDuration =
        payload.duration !== undefined
          ? payload.duration
          : DEFAULT_SNACKBAR_DURATION;
      state.disableClickaway = payload.disableClickaway ?? false;
    },
    showSuccessMessage: (
      state,
      action: PayloadAction<SnackbarMessagePayload>
    ) => {
      const payload =
        typeof action.payload === "string"
          ? {
              message: action.payload,
              duration: DEFAULT_SNACKBAR_DURATION,
              disableClickaway: false,
            }
          : action.payload;
      state.alertMessage = payload.message;
      state.snackbarOpen = true;
      state.alertSeverity = "success";
      state.snackbarAutoCloseDuration =
        payload.duration !== undefined
          ? payload.duration
          : DEFAULT_SNACKBAR_DURATION;
      state.disableClickaway = payload.disableClickaway ?? false;
    },
    showInfoMessage: (state, action: PayloadAction<SnackbarMessagePayload>) => {
      const payload =
        typeof action.payload === "string"
          ? {
              message: action.payload,
              duration: DEFAULT_SNACKBAR_DURATION,
              disableClickaway: false,
            }
          : action.payload;
      state.alertMessage = payload.message;
      state.snackbarOpen = true;
      state.alertSeverity = "info";
      state.snackbarAutoCloseDuration =
        payload.duration !== undefined
          ? payload.duration
          : DEFAULT_SNACKBAR_DURATION;
      state.disableClickaway = payload.disableClickaway ?? false;
    },
    showWarningMessage: (
      state,
      action: PayloadAction<SnackbarMessagePayload>
    ) => {
      const payload =
        typeof action.payload === "string"
          ? {
              message: action.payload,
              duration: DEFAULT_SNACKBAR_DURATION,
              disableClickaway: false,
            }
          : action.payload;
      state.alertMessage = payload.message;
      state.snackbarOpen = true;
      state.alertSeverity = "warning";
      state.snackbarAutoCloseDuration =
        payload.duration !== undefined
          ? payload.duration
          : DEFAULT_SNACKBAR_DURATION;
      state.disableClickaway = payload.disableClickaway ?? false;
    },
    closeSnackbar: (state) => {
      state.snackbarOpen = false;
      state.snackbarAutoCloseDuration = DEFAULT_SNACKBAR_DURATION;
      state.disableClickaway = false;
    },
  },
});

export const {
  showErrorMessage,
  showSuccessMessage,
  showInfoMessage,
  showWarningMessage,
  closeSnackbar,
} = snackbarSlice.actions;

export default snackbarSlice.reducer;
